import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography, OutlinedInput } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { randomId } from '@mui/x-data-grid-generator';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Custom Utils. */
import { MESSAGES, showSnackbar } from '../../../lib/configUtils';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import ThemeButton from 'shared/ui/Button';
import CustomSkeleton from 'shared/ui/Skeleton';

import { getFormulaColumns } from '../TableDef';
import FormulaForm from './FormulaForm';

export default function ItemFormulas({
    userEmail,
    getAccessTokenSilently,
    loadingState,
    setLoadingState,
    itemFormulasRows,
    setItemFormulasRows,
}) {
    const dispatch = useDispatch();

    const [dialogOpen, setDialogOpen] = useState(false);

    const handleDelete = (id) => {
        const isConfirmed = window.confirm('Are you sure you want to delete this formula?');
        if (!isConfirmed) return;
        callAPI({
            method: 'delete',
            module: 'duty_calculator',
            component: 'duty_formula',
            getAccessTokenSilently,
            email: userEmail,
            obj: { id: id },
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: true }));
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: false }));
            },
            onSuccess: (resp) => {
                setItemFormulasRows(itemFormulasRows.filter((row) => row.id !== id));
                showSnackbar(dispatch, MESSAGES.DELETE_SUCCESS, 'success');
            },
            onError: () => {
                showSnackbar(dispatch, MESSAGES.USING_FORMULA, 'warning');
            },
        });
    };

    const handleAddFormula = () => {
        setDialogOpen(true);
    };
    /** Skeleton loading state. */
    if (loadingState.itemFormulas) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <CustomSkeleton />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: 'auto', p: 2 }}>
            <Typography sx={{ fontWeight: 500, fontSize: 16, color: 'text.secondary' }}>Item Formulas</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1.5 }}>
                <ThemeButton text="Add Formula" onClick={handleAddFormula} />
            </Box>
            <DataGridPremium
                rows={itemFormulasRows}
                columns={getFormulaColumns({ onDelete: handleDelete })}
                density="compact"
                disableRowSelectionOnClick
                unstable_cellSelection
                disableColumnMenu
                disableColumnSorting
                processRowUpdate={async (newRow, oldRow) => {
                    try {
                        await new Promise((resolve, reject) => {
                            callAPI({
                                method: 'put',
                                module: 'duty_calculator',
                                component: 'duty_formula',
                                getAccessTokenSilently,
                                email: userEmail,
                                obj: newRow,
                                onInit: () => {
                                    setLoadingState((prev) => ({ ...prev, circularLoading: true }));
                                },
                                onFinal: () => {
                                    setLoadingState((prev) => ({ ...prev, circularLoading: false }));
                                },
                                onSuccess: (resp) => {
                                    setItemFormulasRows((prev) =>
                                        prev.map((row) => (row.id === newRow.id ? resp.data : row)),
                                    );
                                    dispatch(
                                        setSnackbar({
                                            open: true,
                                            message: MESSAGES.UPDATE_SUCCESS,
                                            severity: 'success',
                                        }),
                                    );
                                    resolve();
                                },
                                onError: (error) => {
                                    dispatch(
                                        setSnackbar({
                                            open: true,
                                            message: error.message,
                                            severity: 'error',
                                        }),
                                    );
                                    reject();
                                },
                            });
                        });
                        return newRow;
                    } catch (error) {
                        throw error;
                    }
                }}
                onProcessRowUpdateError={(error) => {
                    console.error('Row update failed:', error);
                }}
                sx={{ width: '100%' }}
            />
            <FormulaForm
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                userEmail={userEmail}
                getAccessTokenSilently={getAccessTokenSilently}
                itemFormulasRows={itemFormulasRows}
                setItemFormulasRows={setItemFormulasRows}
                setLoadingState={setLoadingState}
            />
        </Box>
    );
}
